import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Modal, Form, Button, Alert, Row, Col, Table } from 'react-bootstrap';
import { useFieldConfiguration } from '../../../hooks/useFieldConfiguration';
import FieldConfigurationTable from '../tables/FieldConfigurationTable';
import { validateInput, validateObject } from '../../../utils/sanitizer';
import { getRulesetTypesByCustomer, getExtractionFieldsByRulesetType } from '../../../services/api/rulesetApi';

/**
 * FieldConfigurationSection - Section component for managing field configurations within a rule set context
 * 
 * @param {string} customerId - The ID of the customer
 * @param {string} rulesetId - The ID of the rule set
 */
const FieldConfigurationSection = ({ customerId, rulesetId }) => {
  const {
    fields,
    loading,
    error: apiError,
    createField,
    updateField,
    deleteField,
    importFields,
    exportFields,
    copyFromRuleset
  } = useFieldConfiguration(customerId, rulesetId);

  const [showModal, setShowModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [editingField, setEditingField] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef(null);

  const [fieldForm, setFieldForm] = useState({
    name: '',
    displayName: '',
    fieldKey: '',
    fieldType: 'Header',
    dataType: 'string',
    isRequired: false,
    description: '',
    extractionGuidance: '',
    validationRules: '',
    decimalPrecision: ''
  });

  const [importFile, setImportFile] = useState(null);
  const [copySourceRulesetId, setCopySourceRulesetId] = useState('');
  const [availableRulesets, setAvailableRulesets] = useState([]);
  const [sourceFields, setSourceFields] = useState([]);
  const [selectedFieldsToCopy, setSelectedFieldsToCopy] = useState([]);

  // Fetch available rulesets when component mounts or customerId changes
  useEffect(() => {
    const fetchRulesets = async () => {
      if (customerId) {
        try {
          const response = await getRulesetTypesByCustomer(customerId);
          setAvailableRulesets(response.data || []);
        } catch (err) {
          console.error('Error fetching rulesets:', err);
        }
      }
    };
    
    fetchRulesets();
  }, [customerId]);

  const handleCreate = () => {
    resetForm();
    setShowModal(true);
  };

  const handleEdit = (field) => {
    setEditingField(field);
    
    // Map backend dataType to frontend display value
    let displayDataType = field.dataType || 'string';
    // Keep 'number' as is since it's a valid option in the dropdown
    
    setFieldForm({
      name: field.name || '',
      displayName: field.displayName || '',
      fieldKey: field.fieldKey || '',
      fieldType: field.fieldType === 0 ? 'Header' : field.fieldType === 1 ? 'Detail' : field.fieldType || 'Header',
      dataType: displayDataType,
      isRequired: field.isRequired || false,
      description: field.description || '',
      extractionGuidance: field.extractionGuidance || '',
      validationRules: field.validationRules || '',
      decimalPrecision: field.decimalPrecision != null ? field.decimalPrecision.toString() : ''
    });
    setShowModal(true);
  };

  const handleSaveField = async () => {
    // Validate input
    const validation = validateObject(fieldForm);
    if (!validation.isValid) {
      setError('Input contains potentially unsafe content.');
      return;
    }

    if (!fieldForm.name.trim() || !fieldForm.displayName.trim() || !fieldForm.fieldKey.trim()) {
      setError('Name, Display Name, and Field Key are required.');
      return;
    }

    try {
      // Map dataType to backend-supported values
      let normalizedDataType = fieldForm.dataType;
      if (normalizedDataType === 'integer' || normalizedDataType === 'decimal') {
        normalizedDataType = 'number';
      }
      
      // Convert fieldType string to number for API
      const fieldData = {
        ...fieldForm,
        fieldType: fieldForm.fieldType === 'Header' ? 0 : 1,
        dataType: normalizedDataType,
        decimalPrecision: fieldForm.decimalPrecision !== '' ? parseInt(fieldForm.decimalPrecision, 10) : null,
        extractionRulesetTypeId: parseInt(rulesetId)
      };

      if (editingField) {
        await updateField(editingField.id, { ...fieldData, id: editingField.id });
        setSuccess('Field updated successfully!');
      } else {
        await createField(fieldData);
        setSuccess('Field created successfully!');
      }

      setShowModal(false);
      resetForm();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      // Handle validation errors from backend
      if (err.response?.data?.errors) {
        const validationErrors = err.response.data.errors;
        const errorMessages = Object.entries(validationErrors)
          .map(([field, messages]) => `${field}: ${Array.isArray(messages) ? messages.join(', ') : messages}`)
          .join('; ');
        setError(`Validation failed: ${errorMessages}`);
      } else {
        setError(err.message || 'Failed to save field');
      }
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleDelete = async (fieldId) => {
    if (window.confirm('Are you sure you want to delete this field?')) {
      try {
        await deleteField(fieldId);
        setSuccess('Field deleted successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError(err.message || 'Failed to delete field');
      }
    }
  };

  const handleImport = () => {
    setShowImportModal(true);
  };

  const handleImportSubmit = async () => {
    if (!importFile) {
      setError('Please select a file to import.');
      return;
    }

    try {
      const result = await importFields(importFile);
      setSuccess(`Imported ${result.importedCount} of ${result.totalCount} fields successfully!`);
      setShowImportModal(false);
      setImportFile(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to import fields');
    }
  };

  const handleExport = () => {
    try {
      exportFields();
      setSuccess('Fields exported successfully!');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message || 'Failed to export fields');
    }
  };

  const handleCopy = () => {
    setCopySourceRulesetId('');
    setSourceFields([]);
    setSelectedFieldsToCopy([]);
    setShowCopyModal(true);
  };

  const handleCopySubmit = async () => {
    if (!copySourceRulesetId) {
      setError('Please select a source rule set.');
      return;
    }

    if (selectedFieldsToCopy.length === 0) {
      setError('Please select at least one field to copy.');
      return;
    }

    try {
      const result = await copyFromRuleset(copySourceRulesetId, selectedFieldsToCopy);
      
      let message = `Successfully copied ${result.copiedCount} of ${selectedFieldsToCopy.length} fields`;
      
      if (result.errors && result.errors.length > 0) {
        message += `. Errors: ${result.errors.join(', ')}`;
        setError(message);
      } else {
        setSuccess(message);
      }
      
      setShowCopyModal(false);
      setCopySourceRulesetId('');
      setSourceFields([]);
      setSelectedFieldsToCopy([]);
      setTimeout(() => { setSuccess(''); setError(''); }, 5000);
    } catch (err) {
      setError(err.message || 'Failed to copy fields');
      setTimeout(() => setError(''), 5000);
    }
  };

  const handleSourceRulesetChange = async (rulesetId) => {
    setCopySourceRulesetId(rulesetId);
    
    if (rulesetId) {
      try {
        const response = await getExtractionFieldsByRulesetType(rulesetId);
        const availableFields = response.data || [];
        
        // Filter out fields that already exist in the current ruleset
        const fieldsNotInCurrent = availableFields.filter(sourceField => {
          const existsInCurrent = fields.some(currentField => {
            return currentField.fieldKey && sourceField.fieldKey && 
                   currentField.fieldKey.toLowerCase() === sourceField.fieldKey.toLowerCase();
          });
          return !existsInCurrent;
        });
        
        setSourceFields(fieldsNotInCurrent);
        // Select all available fields by default
        setSelectedFieldsToCopy(fieldsNotInCurrent.map(f => f.id));
      } catch (err) {
        setError('Failed to load fields from source ruleset');
        setTimeout(() => setError(''), 5000);
        setSourceFields([]);
        setSelectedFieldsToCopy([]);
      }
    } else {
      setSourceFields([]);
      setSelectedFieldsToCopy([]);
    }
  };

  const toggleFieldSelection = (fieldId) => {
    setSelectedFieldsToCopy(prev => 
      prev.includes(fieldId) 
        ? prev.filter(id => id !== fieldId)
        : [...prev, fieldId]
    );
  };

  const toggleSelectAll = () => {
    setSelectedFieldsToCopy(prev => 
      prev.length === sourceFields.length ? [] : sourceFields.map(f => f.id)
    );
  };

  const resetForm = () => {
    setEditingField(null);
    setFieldForm({
      name: '',
      displayName: '',
      fieldKey: '',
      fieldType: 'Header',
      dataType: 'string',
      isRequired: false,
      description: '',
      extractionGuidance: '',
      validationRules: '',
      decimalPrecision: ''
    });
    setError('');
  };

  const handleModalClose = () => {
    setShowModal(false);
    resetForm();
  };

  return (
    <div>
      {(error || apiError) && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error || apiError}
        </Alert>
      )}
      {success && (
        <Alert variant="success" onClose={() => setSuccess('')} dismissible>
          {success}
        </Alert>
      )}

      <FieldConfigurationTable
        fields={fields}
        loading={loading}
        onCreate={handleCreate}
        onEdit={handleEdit}
        onDelete={handleDelete}
        onImport={handleImport}
        onExport={handleExport}
        onCopy={handleCopy}
      />

      {/* Create/Edit Field Modal */}
      <Modal show={showModal} onHide={handleModalClose} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{editingField ? 'Edit Field' : 'Add Field'}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Row>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={fieldForm.name}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (!validateInput(value)) {
                        setError('Input contains potentially unsafe content.');
                        return;
                      }
                      setFieldForm({ ...fieldForm, name: value });
                    }}
                    required
                  />
                </Form.Group>
              </Col><Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Display Name *</Form.Label>
                  <Form.Control
                    type="text"
                    value={fieldForm.displayName}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (!validateInput(value)) {
                        setError('Input contains potentially unsafe content.');
                        return;
                      }
                      setFieldForm({ ...fieldForm, displayName: value });
                    }}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={4}>
                <Form.Group className="mb-3">
                  <Form.Label>Field Key *</Form.Label>
                  <Form.Control
                    type="text"
                    value={fieldForm.fieldKey}
                    onChange={(e) => {
                      const value = e.target.value;
                      if (!validateInput(value)) {
                        setError('Input contains potentially unsafe content.');
                        return;
                      }
                      setFieldForm({ ...fieldForm, fieldKey: value });
                    }}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="fieldType">Field Type</Form.Label>
                  <Form.Select
                    id="fieldType"
                    value={fieldForm.fieldType}
                    onChange={(e) => setFieldForm({ ...fieldForm, fieldType: e.target.value })}
                  >
                    <option value="Header">Header</option>
                    <option value="Detail">Detail</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="dataType">Data Type</Form.Label>
                  <Form.Select
                    id="dataType"
                    value={fieldForm.dataType}
                    onChange={(e) => setFieldForm({ ...fieldForm, dataType: e.target.value })}
                  >
                    <option value="string">String</option>
                    <option value="number">Number</option>
                    <option value="date">Date</option>
                    <option value="boolean">Boolean</option>
                  </Form.Select>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="decimalPrecision">Decimal Places</Form.Label>
                  <Form.Control
                    id="decimalPrecision"
                    type="number"
                    min={0}
                    max={6}
                    step={1}
                    value={fieldForm.decimalPrecision}
                    onChange={(e) => setFieldForm({ ...fieldForm, decimalPrecision: e.target.value })}
                    placeholder=""
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group className="mb-3">
                  <Form.Label htmlFor="isRequired">Required</Form.Label>
                  <Form.Check
                    id="isRequired"
                    type="checkbox"
                    checked={fieldForm.isRequired}
                    onChange={(e) => setFieldForm({ ...fieldForm, isRequired: e.target.checked })}
                    className="mt-2"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>
              <Form.Control
                as="textarea"
                rows={2}
                value={fieldForm.description}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!validateInput(value)) {
                    setError('Input contains potentially unsafe content.');
                    return;
                  }
                  setFieldForm({ ...fieldForm, description: value });
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Extraction Guidance</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                value={fieldForm.extractionGuidance}
                onChange={(e) => {
                  const value = e.target.value;
                  if (!validateInput(value)) {
                    setError('Input contains potentially unsafe content.');
                    return;
                  }
                  setFieldForm({ ...fieldForm, extractionGuidance: value });
                }}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Validation Rules (Regex Pattern)</Form.Label>
              <Form.Control
                type="text"
                value={fieldForm.validationRules}
                onChange={(e) => {
                  const pattern = e.target.value;
                  
                  // Clear any previous errors
                  setError('');
                  
                  // Allow empty pattern
                  if (!pattern) {
                    setFieldForm({ ...fieldForm, validationRules: pattern });
                    return;
                  }
                  
                  // Validate regex pattern
                  try {
                    new RegExp(pattern);
                    
                    // Check for dangerous patterns
                    const dangerousPatterns = [
                      /\(\?.*\*.*\+/,
                      /\+.*\*/,
                      /\{\d+,\}/,
                      /\(.*\)\*\+/,
                      /\(.*\)\+\*/
                    ];
                    const isDangerous = dangerousPatterns.some(p => p.test(pattern));
                    
                    if (isDangerous) {
                      setError('Potentially unsafe regex pattern detected. Please use a simpler pattern.');
                      // Don't update the field state with invalid pattern
                      return;
                    }
                    
                    // Pattern is valid and safe, update the form
                    setFieldForm({ ...fieldForm, validationRules: pattern });
                  } catch (err) {
                    setError('Invalid regex pattern. Please check your syntax.');
                    // Don't update the field state with invalid pattern
                    return;
                  }
                }}
                placeholder="Enter regex pattern for validation (optional)"
                maxLength={500}
                isInvalid={error && error.includes('regex')}
              />
              <Form.Control.Feedback type="invalid">
                {error && error.includes('regex') ? error : ''}
              </Form.Control.Feedback>
              <Form.Text className="text-muted">
                Optional regex pattern to validate extracted data (max 500 characters). Avoid complex nested patterns.
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleModalClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleSaveField}
            disabled={!fieldForm.name.trim() || !fieldForm.displayName.trim() || !fieldForm.fieldKey.trim()}
          >
            {editingField ? 'Update' : 'Create'}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Import Modal */}
      <Modal show={showImportModal} onHide={() => setShowImportModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Import Fields</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group>
            <Form.Label>Select JSON File</Form.Label>
            <Form.Control
              type="file"
              accept=".json"
              ref={fileInputRef}
              onChange={(e) => setImportFile(e.target.files[0])}
            />
            <Form.Text className="text-muted">
              Upload a JSON file containing field configurations.
            </Form.Text>
          </Form.Group>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowImportModal(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleImportSubmit} disabled={!importFile}>
            Import
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Copy from Rule Set Modal */}
      <Modal show={showCopyModal} onHide={() => setShowCopyModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>Copy Fields from Another Ruleset</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!copySourceRulesetId || sourceFields.length === 0 ? (
            <Form.Group className="mb-3">
              <Form.Label>Select Source Ruleset</Form.Label>
              <Form.Select
                value={copySourceRulesetId}
                onChange={(e) => handleSourceRulesetChange(e.target.value)}
              >
                <option value="">Choose a ruleset to copy from...</option>
                {availableRulesets
                  .filter(ruleset => ruleset.id.toString() !== rulesetId)
                  .map(ruleset => (
                    <option key={ruleset.id} value={ruleset.id}>
                      {ruleset.name}
                    </option>
                  ))}
              </Form.Select>
              <Form.Text className="text-muted">
                {copySourceRulesetId && sourceFields.length === 0 ? 
                  'The selected ruleset has no field configurations to copy.' :
                  'Only fields that don\'t already exist in the current ruleset will be available for copying.'
                }
              </Form.Text>
            </Form.Group>
          ) : (
            <div>
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6>Available Fields to Copy ({sourceFields.length})</h6>
                <div>
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="me-2"
                    onClick={toggleSelectAll}
                  >
                    {selectedFieldsToCopy.length === sourceFields.length ? 'Deselect All' : 'Select All'}
                  </Button>
                  <Button 
                    variant="outline-secondary" 
                    size="sm"
                    onClick={() => {
                      setCopySourceRulesetId('');
                      setSourceFields([]);
                      setSelectedFieldsToCopy([]);
                    }}
                  >
                    Change Source
                  </Button>
                </div>
              </div>
              
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th width="50">
                        <Form.Check 
                          type="checkbox"
                          checked={selectedFieldsToCopy.length === sourceFields.length}
                          onChange={toggleSelectAll}
                        />
                      </th>
                      <th>Name</th>
                      <th>Field Key</th>
                      <th>Type</th>
                      <th width="120">Data Type</th>
                      <th>Required</th>
                      <th>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sourceFields.map(field => (
                      <tr key={field.id}>
                        <td>
                          <Form.Check 
                            type="checkbox"
                            checked={selectedFieldsToCopy.includes(field.id)}
                            onChange={() => toggleFieldSelection(field.id)}
                          />
                        </td>
                        <td>{field.displayName || field.name}</td>
                        <td>{field.fieldKey}</td>
                        <td>{field.fieldType === 0 ? 'Header' : 'Detail'}</td>
                        <td>{field.dataType}</td>
                        <td>{field.isRequired ? 'Yes' : 'No'}</td>
                        <td>{field.description || ''}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCopyModal(false)}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={handleCopySubmit}
            disabled={selectedFieldsToCopy.length === 0 || sourceFields.length === 0}
          >
            Copy {selectedFieldsToCopy.length} Selected Fields
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

FieldConfigurationSection.propTypes = {
  customerId: PropTypes.string.isRequired,
  rulesetId: PropTypes.string.isRequired
};

export default FieldConfigurationSection;
